/**
 * reporter.ts — client-side RUM event reporter.
 *
 * Collects RUM events into a bounded in-memory buffer, then flushes via
 * navigator.sendBeacon (fallback: fetch keepalive) to the same-origin /rum
 * relay on visibilitychange(hidden) + pagehide.
 *
 * PII Controls:
 *   - Hard ALLOW-LIST: only the keys in ALLOWED_KEYS survive serialisation.
 *   - strip() removes query strings and hashes from any accidental path value.
 *   - route_template is the ONLY path-derived attribute; raw pathnames are NEVER
 *     buffered or transmitted.
 *   - No identity, session, inviteToken, api_key, db_uri, query, JWT.
 *
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Hard attribute allow-list. Anything outside this set is dropped. */
export const ALLOWED_KEYS = [
  "type",
  "name",
  "value",
  "rating",
  "route_template",
  "nav_type",
  "status_class",
  "error_type",
] as const;

export type AllowedKey = (typeof ALLOWED_KEYS)[number];

/** A raw (pre-allow-list) event payload. Keys outside ALLOWED_KEYS are dropped. */
export type RumEventInput = Partial<Record<AllowedKey, string | number>>;

/** A scrubbed, allow-listed event ready for transmission. */
export type RumEvent = Partial<Record<AllowedKey, string | number>>;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum buffered events — prevents unbounded growth on long sessions. */
const MAX_BUFFER_SIZE = 200;

/** Endpoint for the same-origin relay (non-/api so it is not proxied). */
const RUM_ENDPOINT = "/rum";

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let buffer: RumEvent[] = [];
let flushScheduled = false;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Apply the hard ALLOW-LIST to a raw event.
 * Drops every key not in ALLOWED_KEYS. Returns undefined if nothing remains.
 */
export function applyAllowList(raw: RumEventInput): RumEvent | undefined {
  const out: RumEvent = {};
  let hasAny = false;
  for (const key of ALLOWED_KEYS) {
    if (key in raw && raw[key] !== undefined && raw[key] !== null) {
      (out as Record<string, string | number>)[key] = raw[key] as
        | string
        | number;
      hasAny = true;
    }
  }
  return hasAny ? out : undefined;
}

/**
 * Strip query string and fragment from a string value.
 * Used defensively on any string field that might accidentally carry a URL.
 */
export function stripQueryAndHash(v: string): string {
  return v.split("?")[0]!.split("#")[0]!;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Buffer a RUM event after applying the allow-list.
 * Silently no-ops outside a browser context (SSR/edge guard).
 */
export function reportRumEvent(raw: RumEventInput): void {
  // Guard: not a browser environment (SSR, edge, tests without jsdom window).
  if (typeof window === "undefined") return;

  const event = applyAllowList(raw);
  if (!event) return;

  // Enforce bounded buffer — drop oldest when full.
  if (buffer.length >= MAX_BUFFER_SIZE) {
    buffer.shift();
  }
  buffer.push(event);
}

/**
 * Flush the buffered events via sendBeacon (→ fetch keepalive fallback).
 * Idempotent: second call while in-flight is a no-op (buffer already drained).
 */
export function flushRumBuffer(): void {
  if (typeof window === "undefined") return;
  if (buffer.length === 0) return;

  // Drain atomically — take a snapshot so concurrent calls don't double-send.
  const snapshot = buffer;
  buffer = [];
  flushScheduled = false;

  const body = JSON.stringify({ events: snapshot });
  const blob = new Blob([body], { type: "application/json" });

  // Primary: sendBeacon (fire-and-forget, survives page unload).
  if (navigator.sendBeacon) {
    const queued = navigator.sendBeacon(RUM_ENDPOINT, blob);
    if (queued) return;
  }

  // Fallback: fetch with keepalive (survives visibilitychange in most browsers).
  void fetch(RUM_ENDPOINT, {
    method: "POST",
    keepalive: true,
    body,
    headers: { "Content-Type": "application/json" },
  }).catch(() => {
    // Intentionally swallow — page may already be unloading.
  });
}

/**
 * Register the flush listeners (visibilitychange + pagehide).
 * Idempotent: safe to call multiple times (guards with flushScheduled flag).
 * Call once from useEffect in WebVitalsReporter.
 */
export function registerFlushListeners(): void {
  if (typeof window === "undefined") return;
  if (flushScheduled) return;
  flushScheduled = true;

  const onHidden = () => {
    if (document.visibilityState === "hidden") {
      flushRumBuffer();
    }
  };

  document.addEventListener("visibilitychange", onHidden);
  window.addEventListener("pagehide", flushRumBuffer);
}

/**
 * TEST SEAM: reset internal buffer + registration state.
 * Do NOT call in production code.
 */
export function __resetReporterForTests(): void {
  buffer = [];
  flushScheduled = false;
}
